import { useEffect, useState } from 'react';
import { permissionsApi, type PermissionDto } from '@api/permissions';
import { rolesApi, type RoleDto } from '@api/roles';
import { HttpError } from '@api/http';

const HIDDEN_PERMISSION_KEYS = new Set(['role_management', 'camera_group_crud']);
const PERMISSION_LABEL_OVERRIDES: Record<string, string> = {
  user_management: 'User & Role Management',
  camera_crud: 'Camera and Camera Group Management',
  recording_control: 'Recording Control & Global Settings',
};

export default function RolesAdminPage(): JSX.Element {
  const [roles, setRoles] = useState<RoleDto[]>([]);
  const [permissions, setPermissions] = useState<PermissionDto[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [createName, setCreateName] = useState('');
  const [createSlug, setCreateSlug] = useState('');
  const [busy, setBusy] = useState(false);
  const visiblePermissions = permissions.filter((permission) => !HIDDEN_PERMISSION_KEYS.has(permission.key));

  const load = async (): Promise<void> => {
    const [nextRoles, nextPermissions] = await Promise.all([rolesApi.list(), permissionsApi.list()]);
    setRoles(nextRoles);
    setPermissions(nextPermissions);
  };

  useEffect(() => {
    load().catch((err: unknown) => {
      const message =
        err instanceof HttpError ? err.message : err instanceof Error ? err.message : 'Failed to load roles';
      setError(message);
    });
  }, []);

  return (
    <div className="mx-auto flex w-full max-w-screen-xl flex-col gap-4 p-6">
      <h1 className="text-xl font-semibold">Roles</h1>
      {error ? <p className="text-sm text-danger">{error}</p> : null}

      <form
        className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface p-3"
        onSubmit={(e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          rolesApi
            .create({ name: createName, slug: createSlug })
            .then(() => {
              setCreateName('');
              setCreateSlug('');
              return load();
            })
            .catch((err: unknown) => {
              const message =
                err instanceof HttpError
                  ? err.message
                  : err instanceof Error
                    ? err.message
                    : 'Failed to create role';
              setError(message);
            })
            .finally(() => setBusy(false));
        }}
      >
        <input
          className="rounded border border-border bg-bg px-2 py-1.5 text-sm"
          placeholder="Role name"
          value={createName}
          onChange={(e) => setCreateName(e.target.value)}
          required
        />
        <input
          className="rounded border border-border bg-bg px-2 py-1.5 text-sm"
          placeholder="role_slug"
          value={createSlug}
          onChange={(e) => setCreateSlug(e.target.value)}
          required
        />
        <button
          type="submit"
          disabled={busy}
          className="rounded bg-accent px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60"
        >
          {busy ? 'Creating...' : 'Create role'}
        </button>
      </form>

      <div className="space-y-3">
        {roles.map((role) => (
          <section key={role.id} className="rounded-md border border-border bg-surface p-3">
            <div className="mb-2 flex items-center justify-between">
              <div>
                <h2 className="font-medium">
                  {role.name} <span className="text-xs text-muted">({role.slug})</span>
                </h2>
                {role.isSystem ? (
                  <p className="text-xs text-muted">System role (cannot be deleted).</p>
                ) : null}
              </div>
              {!role.isSystem ? (
                <button
                  type="button"
                  className="rounded border border-border px-2 py-1 text-xs hover:border-danger hover:text-danger"
                  onClick={() => {
                    rolesApi
                      .remove(role.id)
                      .then(() => load())
                      .catch((err: unknown) => {
                        const message =
                          err instanceof HttpError
                            ? err.message
                            : err instanceof Error
                              ? err.message
                              : 'Failed to delete role';
                        setError(message);
                      });
                  }}
                >
                  Delete
                </button>
              ) : null}
            </div>
            <div className="grid gap-2 md:grid-cols-3">
              {visiblePermissions.map((permission) => {
                const checked =
                  role.permissions?.some((item) => item.key === permission.key) ?? false;
                const disabled = role.slug === 'super_admin';
                return (
                  <label key={permission.key} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={(e) => {
                        const nextKeys = new Set((role.permissions ?? []).map((item) => item.key));
                        if (e.target.checked) nextKeys.add(permission.key);
                        else nextKeys.delete(permission.key);

                        rolesApi
                          .replacePermissions(role.id, [...nextKeys])
                          .then(() => load())
                          .catch((err: unknown) => {
                            const message =
                              err instanceof HttpError
                                ? err.message
                                : err instanceof Error
                                  ? err.message
                                  : 'Failed to update role permissions';
                            setError(message);
                          });
                      }}
                    />
                    <span>{PERMISSION_LABEL_OVERRIDES[permission.key] ?? permission.label}</span>
                  </label>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
